import { html, render, useEffect, useErrorBoundary, useRef, useState }
  from '/vendor/htm-preact.module.js';
import { state } from '/auth.js';
import { post } from '/api.js';
import { flush } from '/queue.js';
import { SignIn } from '/screens.js';
import { Record } from '/record.js';
import { HeadToHead, Leaderboard, Progression } from '/stats.js';
import { Live } from '/live.js';
import { KillerScreen } from '/killerscreen.js';
import { KnockoutScreen } from '/knockout.js';
import { JoinQueue } from '/joinqueue.js';
import { LeaguesScreen, LeagueScreen, FixtureScreen } from '/leagues.js';
import { CupScreen } from '/cup.js';
import { PlayerScreen } from '/player.js';
import { TeamScreen } from '/team.js';
import { parse, path, pageTag } from '/router.js';
import { Shell, TABS } from '/shell.js';
import { LeagueTab } from '/league.js';
import { CircleTab, CircleScreen } from '/circle.js';
import { AdminScreen } from '/admin.js';
import { NotificationsScreen } from '/notify_history.js';
import { SupportScreen } from '/support.js';
import { VenuesTab } from '/venues.js';
import { VenueScreen } from '/venue.js';
import { WatchScreen } from '/watch.js';
import { MatchesScreen } from '/history.js';
import { Kiosk } from '/kiosk.js';
import { prefetch } from '/read.js';
import { useCircleChoice } from '/circlechoice.js';
import { SettingsTab } from '/settings.js';
import { Rulebook } from '/rulebook.js';

// Proves the vendored module actually loaded. A blank screen because an ES
// module 404'd is otherwise a silent, baffling failure.
window.__runtimeReady = true;

// Shown on the two screens that mean "nothing has happened yet". A stuck app
// is the one time you cannot tell whether a phone is running what was actually
// deployed, and a whole afternoon went into inferring that from access logs.
// Bump it when deploying something you need to confirm arrived.
export const BUILD = '2026-08-11c';
window.__build = BUILD;

function App() {
  const [authState, setAuthState] = useState(null);
  const [failed, setFailed] = useState(null);
  // The URL says which screen this is — not a piece of state kept alongside
  // it. Two sources for one fact drift, and the one that loses is always the
  // URL, which is how an app ends up with a back button that closes it.
  const [route, setRoute] = useState(() => parse(location.hash));
  // Which circle the numbers are for, known here only so the two screens whose
  // reads depend on it can be warmed as well. Reads from the shared cache, so
  // it costs nothing that the screens were not already paying.
  const [, warmCircleId] = useCircleChoice();
  const { screen, id, view, sub, watchToken, queueToken } = route;

  // Navigating means changing the URL, AND moving the screen in the same tick.
  //
  // Waiting for the hashchange listener alone was one path for both the back
  // gesture and the buttons, which is why it was written that way — but
  // hashchange fires in a later task, so every tap left the old screen drawn
  // and the old tab highlighted until it arrived. A visible flash of the menu
  // you just left, on every navigation.
  //
  // Both still derive the route the same way, from parse() of the same URL, so
  // there is still one rule about what a URL means; this just stops waiting to
  // be told something it already knows. A listener firing afterwards parses
  // the same string and sets the same route.
  //
  // `replace` for a screen there must be no going BACK to: the form that
  // starts a match is spent the moment it succeeds, and returning to it would
  // offer to start a second one. replaceState fires no hashchange at all.
  const cameFrom = useRef(null);

  const go = (where, id, viewOrOpts, subOrOpts, opts) => {
    const view = typeof viewOrOpts === 'string' ? viewOrOpts : null;
    // A fourth id, for the one route that needs one: a cup is named by its
    // season AND its competition (see router.js). Everything else passes
    // three arguments and never reaches this.
    const sub = typeof subOrOpts === 'string' ? subOrOpts : null;
    const { replace = false } = (typeof viewOrOpts === 'object' ? viewOrOpts
      : typeof subOrOpts === 'object' ? subOrOpts : opts) || {};
    const next = path(where, id, view, sub);
    if (!replace) {
      // Where BACK should land, and WHICH screen that answer belongs to.
      //
      // A screen reached from two places has two right answers -- a team
      // opened from the standings goes back to the standings, the same team
      // opened from its pub goes back to the pub -- and a hardcoded onDone
      // can only be right about one. NOT set on `replace`, which exists
      // precisely for a screen there must be no going back to.
      //
      // `to` is not decoration. Without it this remembers the last
      // navigation ANYWHERE, so a deep link followed by an unrelated
      // `go()` -- `refresh()` calls `go('home')` after sign-in -- left BACK
      // pointing at a screen you never came through. That raced: same test,
      // 1 run in 5, and by timeout rather than a wrong assertion.
      cameFrom.current = { from: location.hash || '#/', to: next };
      location.hash = next;
      setRoute(parse(next));
      return;
    }
    history.replaceState(null, '', next);
    setRoute(parse(next));
  };

  // One step back, to wherever you actually came from, falling back to the
  // screen that owns this one. The fallback is not decoration: a team page
  // opened from a pasted link has nowhere to go back TO, and its league is
  // the honest answer there.
  const backOne = (fallback) => {
    const step = cameFrom.current;
    cameFrom.current = null;
    // Only if that navigation landed on the screen we are actually leaving.
    // Anything else -- a deep link, or a later go() somewhere unrelated --
    // has no remembered way back, and the fallback is the honest answer.
    if (step && step.to === location.hash && step.from !== location.hash) {
      location.hash = step.from;
      setRoute(parse(step.from));
      return;
    }
    fallback();
  };

  // A failure here used to leave `authState` null for ever, and null renders
  // as "Loading…" — so a phone that had lost the tailnet sat on that word with
  // no explanation and no way out. The shell is cached and the API deliberately
  // is not, so this is the one call that is always live, and it has to be able
  // to say it did not land.
  const load = async () => {
    try {
      setFailed(null);
      setAuthState(await state());
    } catch (e) {
      setFailed(e.detail || e.message || 'no answer');
    }
  };
  // Signing in or out returns you to the front; opening the app does NOT —
  // it honours whatever the URL asked for, which is the whole point of having
  // one.
  const refresh = async () => { await load(); go('home'); };

  useEffect(() => {
    // REPLACE, so the first entry in the history stack is already '#/'. Pushing
    // it instead would leave a bare '/' underneath, and the first press of back
    // would appear to do nothing at all.
    if (!location.hash) history.replaceState(null, '', '#/');
    load();
  }, []);

  useEffect(() => {
    const onHash = () => setRoute(parse(location.hash));
    // Re-read on ATTACH, not just on the event. `route` is seeded from
    // location.hash during the first render, and this listener goes on
    // afterwards -- so a hash that changed in between fired a hashchange
    // with nobody listening, and the app sat on the screen it first
    // rendered. Rare, and exactly what a deep link races: measured as one
    // run in three failing to render a team page opened by URL, timing out
    // rather than landing anywhere wrong.
    onHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // The other tabs, warmed once this one has drawn.
  //
  // A tab you have not opened yet costs a round trip you can see; the same
  // round trip taken while nobody is waiting costs nothing. Idle, so it never
  // competes with the screen actually in front of somebody, and only what a
  // tab needs before it can show anything — the per-circle reads still happen
  // on arrival, because which circle is a choice this does not get to make.
  useEffect(() => {
    if (!authState || !authState.signed_in) return undefined;
    const warm = () => {
      // '/api/leagues' is here because Leagues is a TAB now — it took the
      // slot Leaders had. On the old second row it could afford to fetch on
      // arrival; one of four primary destinations cannot, or it is the one
      // that blinks "Loading…" while the other three paint instantly.
      ['/api/me/circles', '/api/me/pending', '/api/people', '/api/venues',
       '/api/requests', '/api/discover', '/api/leagues'].forEach(prefetch);
      // The board and the dial read per circle, so warming the shared lists
      // alone left exactly those two still waiting. The circle is the one
      // already chosen — no guess, and wrong only if you switch, which
      // refetches anyway.
      if (warmCircleId) {
        const q = `?circle_id=${encodeURIComponent(warmCircleId)}`;
        prefetch(`/api/circles/${encodeURIComponent(warmCircleId)}/leaderboard`);
        prefetch(`/api/players/${authState.user_id}/progression${q}`);
      }
    };
    const idle = window.requestIdleCallback
      ? requestIdleCallback(warm, { timeout: 2000 })
      : setTimeout(warm, 300);
    return () => (window.cancelIdleCallback
      ? cancelIdleCallback(idle) : clearTimeout(idle));
  }, [authState && authState.signed_in, warmCircleId]);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js');
    }
    flush();                          // anything left over from last time
    const onOnline = () => flush();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  if (!authState) {
    if (!failed) {
      return html`
        <p class="sub">Loading…</p>
        <p class="sub" data-testid="build" style="font-size:.7rem;opacity:.5">
          ${BUILD}
        </p>`;
    }
    return html`
      <h1>Cueforge</h1>
      <p class="error" data-testid="boot-error">
        ${`Could not reach Cueforge — ${failed}.`}
      </p>
      <p class="sub" data-testid="build" style="font-size:.7rem;opacity:.5">
        ${BUILD}
      </p>
      <p class="sub">
        The app itself is stored on your phone, but who you are is not, and
        that answer has to come from the server. Check you are on the tailnet
        and try again.
      </p>
      <button data-testid="boot-retry" onClick=${load}>Try again</button>`;
  }
  // Said once, at the top of whatever screen you are on, because the mismatch
  // it reports can break any of them and the symptom is never obvious. Not a
  // blocking screen: everything the running server does understand still works,
  // and shutting the app down over it would be worse than the fault.
  const stale = authState.server_stale && html`
    <p class="error" data-testid="server-stale">
      This server is running older code than is installed — restart it, or
      parts of the app will not match what it can answer.
    </p>`;


  // Said on every screen, and deliberately not dismissable: a session that
  // silently answers as somebody else is the one thing this feature must
  // never be. It carries the way out with it, because a banner that only
  // announces the state and makes you go and find the switch is half a
  // feature. Writes are refused server-side while this is up (see `actor`
  // in api.py) -- the wording says so rather than letting somebody discover
  // it by pressing something and getting a 403.
  const viewing = authState.viewing_as && html`
    <p class="error" data-testid="viewing-as">
      Viewing as ${authState.viewing_as} — reading only, nothing can be
      recorded.
      <button class="secondary" style="width:auto"
              data-testid="viewing-as-stop"
              onClick=${async () => {
                await post('/api/admin/view-as', { user_id: null });
                refresh();
              }}>Back to ${authState.really_me}</button>
    </p>`;

  // Before the sign-in gate, deliberately. A watcher who scanned the QR by the
  // table has no account — that is the whole point of the token — and sending
  // them to a login screen would defeat the feature entirely.
  if (screen === 'watch') {
    return html`<${WatchScreen} token=${watchToken} />`;
  }
  if (!authState.signed_in) {
    // The queue link is AFTER the gate, unlike the watch link: the point of
    // it is to join as somebody, and there is nobody to put in the line
    // without a session. Signing in first is the feature, not an obstacle.
    return html`${stale}${viewing}<${SignIn} authState=${authState} onChanged=${refresh} />`;
  }
  const back = () => go('home');
  if (screen === 'league') {
    // The room travels in the URL. Held as component state it survived
    // nothing: back from a team landed on the right league and the wrong
    // room, a reload dropped you on Home, and a room could not be linked
    // to at all — which is the case parse() already argues for.
    return html`<${LeagueScreen} leagueId=${id} room=${view} onGo=${go}
                  onDone=${() => go('leagues')} />`;
  }
  // Its own screen, not a view of the league: a fixture is scored frame by
  // frame and wants the whole window the way a match does, and "back" means
  // where you came from -- the Match Hub if you opened it there, the team's
  // page if you opened it there (issue #79).
  //
  // `backOne` was already the answer and this was the one league screen not
  // asking it, so back always landed on league Home however you arrived. It
  // showed up the day issue #74 made the hub's rows pressable: until then
  // almost nobody reached a fixture from the hub, so almost nobody was
  // returned to the wrong place. The fallback stays the league, which is the
  // honest answer for a fixture opened from a pasted link with no step to
  // remember.
  if (screen === 'fixture' && id) {
    return html`<${FixtureScreen} leagueId=${id} fixtureId=${view}
                  onDone=${() => backOne(() => go('league', id))} />`;
  }
  // One competition: its entrants, its results, and its bracket. Its own
  // screen for the same reason `fixture` and `player` are -- back means the
  // league it belongs to, and specifically the Competitions room it was
  // opened from, which `backOne` answers when there is a step to remember.
  if (screen === 'cup' && id && view && sub) {
    return html`<${CupScreen} leagueId=${id} season=${view} competition=${sub}
                  onGo=${go}
                  onDone=${() => backOne(() => go('league', id, 'cups'))} />`;
  }
  // Its own screen, not a view of the league, the same reasoning as
  // `fixture` just above -- and it had the same omission. A player opened
  // from the Stats board, from a team's roster or from a fixture's sheet has
  // three right answers for back, and a hardcoded one can only be right
  // about the first.
  if (screen === 'player' && id) {
    return html`<${PlayerScreen} leagueId=${id} playerKey=${view}
                  onGo=${go}
                  onDone=${() => backOne(() => go('league', id))} />`;
  }
  // Its own screen, not a view of the league, the same reasoning as
  // `fixture` and `player` just above: back means the league it belongs to.
  if (screen === 'team' && id) {
    return html`<${TeamScreen} leagueId=${id} circleId=${view}
                  onGo=${go}
                  onDone=${() => backOne(() => go('league', id))} />`;
  }
  if (screen === 'joinqueue') {
    return html`<${JoinQueue} token=${queueToken} me=${authState.user_id}
                  onGo=${go} onDone=${back} />`;
  }

  // A tablet bolted to a pub wall is equipment, not a person with the personal
  // bits hidden — one branch here rather than a kind check in the gauge, the
  // tab bar, the brand row, the record flow and settings. It still gets the
  // live screen, because the thing it exists to do is open a match and then
  // score it.
  if (authState.kind === 'device') {
    if (screen === 'live') {
      return html`<${Live} matchId=${id} me=${authState.user_id}
                    kiosk onDone=${back} />`;
    }
    if (screen === 'killer') {
      return html`<${KillerScreen} killerId=${id} me=${authState.user_id}
                    kiosk onDone=${back} />`;
    }
    if (screen === 'knockout') {
      return html`<${KnockoutScreen} knockoutId=${id} me=${authState.user_id}
                    kiosk onDone=${back} />`;
    }
    // The rules, reachable. The match screen's "?" goes to #/rules, and
    // everything that was not 'live' fell through to the kiosk — so on a
    // tablet that button silently abandoned the match instead of settling
    // the argument it was pressed to settle.
    if (screen === 'rules') {
      return html`<${Rulebook} onDone=${() => history.back()} />`;
    }
    return html`${stale}${viewing}<${Kiosk} authState=${authState} onGo=${go} />`;
  }

  // Screens that take the whole window. A match, or a form on the way to one,
  // is not somewhere you want a tab bar tempting a thumb mid-frame.
  if (screen === 'live') {
    return html`<${Live} matchId=${id} me=${authState.user_id}
                  onDone=${back} />`;
  }
  if (screen === 'killer') {
    return html`<${KillerScreen} killerId=${id} me=${authState.user_id}
                  onDone=${back} />`;
  }
  if (screen === 'knockout') {
    return html`<${KnockoutScreen} knockoutId=${id} me=${authState.user_id}
                  onDone=${back} />`;
  }
  if (screen === 'record') {
    return html`<${Record} onDone=${back}
                  onStarted=${(id) => go('live', id, { replace: true })} />`;
  }
  // Its own screen rather than an overlay: back closes it, and coming back
  // to a match re-folds the frame from the shots already posted.
  if (screen === 'rules') {
    return html`<${Rulebook} onDone=${() => history.back()} />`;
  }
  // A circle and a venue are places, each at its own URL — so they can be
  // linked to, reloaded, and left with the back button. Dispatched before the
  // tab fallback, which would otherwise send them to their own list.
  if (screen === 'circle' && id) {
    return html`<${CircleScreen} authState=${authState} circleId=${id}
                  view=${view} onGo=${go} onDone=${() => go('circle')} />`;
  }
  if (screen === 'venue' && id) {
    // `view` carries the manage room (issue #28), the same second segment
    // a circle's own manage page already uses.
    return html`<${VenueScreen} venueId=${id} view=${view} onGo=${go}
                  onDone=${() => go('venue')} />`;
  }
  // Matches already played: yours, or one circle's.
  if (screen === 'matches') {
    return html`<${MatchesScreen} circleId=${id} onGo=${go}
                  onDone=${() => (id ? go('circle', id) : back())} />`;
  }
  if (screen === 'admin') {
    // `refresh` as well as `back`: starting a view-as changes who the SESSION
    // answers as, and `back` only navigates. Without it the switch took
    // effect on the server and the banner never appeared, because nothing
    // re-read /api/auth/state.
    return html`<${AdminScreen} onDone=${back} onGo=${go}
                  onSwitched=${refresh} />`;
  }
  if (screen === 'h2h') {
    return html`<${HeadToHead} onDone=${back} circleId=${id} />`;
  }
  if (screen === 'progress') {
    return html`<${Progression} onDone=${back} userId=${authState.user_id} />`;
  }

  // The four tabs. `where` is the line under the wordmark: in the mockup it
  // names the league, and here it names who is signed in — the one thing that
  // is always true and worth checking at a glance.
  // Settings is no longer a tab — it lives behind the account icon — so it is
  // dispatched before the tab fallback, which would otherwise send it home.
  if (screen === 'settings') {
    return html`
      ${stale}${viewing}
      <${Shell} screen=${screen} where=${authState.name} onGo=${go}>
        <${SettingsTab} authState=${authState} onChanged=${refresh}
                        onReload=${load} onGo=${go} build=${BUILD} />
      <//>`;
  }

  // What you were told while you were not looking — GitHub issue #49.
  // Dispatched here beside Settings and for the same reason: it is reached
  // from the chrome rather than from a tab, so the tab fallback below would
  // send it Home. It KEEPS the shell, because the bell that opens it lives
  // there and a screen you reach from the chrome should not remove it.
  // Beside Notifications and for its reasons: reached from the chrome, so
  // the tab fallback would send it Home, and it KEEPS the shell because the
  // envelope that opens it lives there. Issue #67.
  if (screen === 'support') {
    return html`
      ${stale}${viewing}
      <${Shell} screen=${screen} where=${authState.name} onGo=${go}>
        <${SupportScreen} onDone=${back} />
      <//>`;
  }

  if (screen === 'notifications') {
    return html`
      ${stale}${viewing}
      <${Shell} screen=${screen} where=${authState.name} onGo=${go}>
        <${NotificationsScreen} onDone=${back} onGo=${go} />
      <//>`;
  }

  // The Leaderboard is no longer a tab — Leagues took that slot — so like
  // Settings it is dispatched before the tab fallback, which would otherwise
  // send it Home and leave the button on Home pointing at the wrong screen.
  // It keeps the bar: it is somewhere you go from Home and come back from,
  // not a place you leave the app's chrome for.
  if (screen === 'board') {
    return html`
      ${stale}${viewing}
      <${Shell} screen=${screen} where=${authState.name} onGo=${go}>
        <${Leaderboard} onDone=${back} />
      <//>`;
  }

  // The four tabs. Anything else falls back to Home rather than to a blank
  // shell.
  const rows = TABS.filter((t) => t.screen);
  const inTab = rows.some((t) => t.screen === screen) ? screen : 'home';
  return html`
    ${stale}${viewing}
    <${Shell} screen=${inTab} where=${authState.name} onGo=${go}>
      ${inTab === 'circle'
        ? html`<${CircleTab} authState=${authState} onGo=${go} />`
        : inTab === 'venue'
          ? html`<${VenuesTab} onGo=${go} />`
          : inTab === 'leagues'
            ? html`<${LeaguesScreen} onGo=${go} />`
            : html`<${LeagueTab} authState=${authState} onGo=${go} />`}
    <//>`;
}

/**
 * Nothing may fail silently.
 *
 * A render that throws leaves the DOM exactly as it was, so the app freezes on
 * whatever it last drew — usually the word "Loading…" — with no error, no
 * console for a phone, and nothing to act on. api.js already guards the other
 * road to that same screen, a fetch that never settles; this is the one that
 * comes from the code itself.
 *
 * It happened for real: StaticFiles reads the web directory from disk on every
 * request, so a server left running while the working tree moved on serves
 * today's JavaScript from a process that imported this morning's Python. The
 * new venue page read a field the old endpoint did not send and died mid-render.
 */
function Crashed({ crash, onReset }) {
  return html`
    <div class="screen">
      <h1>Cueforge</h1>
      <p class="error" data-testid="crash">
        Something on that screen could not be drawn.
      </p>
      <p class="sub" data-testid="crash-detail">
        ${(crash && (crash.message || String(crash))) || 'no detail'}
      </p>
      <p class="sub" data-testid="build" style="font-size:.7rem;opacity:.5">
        ${BUILD}
      </p>
      <p class="sub">
        If this started after an update, the app and the server may be different
        versions — reloading picks up the current one.
      </p>
      <button data-testid="crash-home" onClick=${() => {
        location.hash = '#/';
        onReset();
      }}>Back to the start</button>
      <button class="secondary" data-testid="crash-reload"
              onClick=${() => location.reload()}>Reload</button>
    </div>`;
}

/**
 * The screen's own name, in the corner of every page.
 *
 * So a screen can be POINTED AT. "The venue page" is four screens now, and
 * saying which one costs a paragraph and a screenshot; "VENUE/INFO" costs two
 * words and cannot be misread. It is in the corner of a screenshot too, which
 * is how most of them arrive.
 *
 * Derived from the route (see router.js), so a screen added tomorrow is
 * labelled tomorrow without anybody maintaining a list.
 *
 * Rendered here rather than inside each screen: every screen would have to
 * remember, and the ones that forgot would be exactly the ones nobody could
 * name.
 */
function PageTag() {
  const [tag, setTag] = useState(() => pageTag(parse(location.hash)));
  useEffect(() => {
    const onHash = () => setTag(pageTag(parse(location.hash)));
    addEventListener('hashchange', onHash);
    return () => removeEventListener('hashchange', onHash);
  }, []);
  return html`<span class="pagetag" data-testid="page-tag">${tag}</span>`;
}

function Root() {
  const [crash, onReset] = useErrorBoundary();
  if (crash) return html`<${Crashed} crash=${crash} onReset=${onReset} />`;
  return html`<${App} /><${PageTag} />`;
}

render(html`<${Root} />`, document.getElementById('app'));
